import React, { useState } from "react";
import { DynamicField } from "../types/DynamicField";
import "../styles/FieldBuilder.css";
import { Section } from "../types/Section";
import { FIELD_TYPES, FIELD_TYPES_LIST, FieldTypeObject } from "../types/FieldTypes";
import { FIELD_BASE_TYPES, FIELD_BASE_TYPES_LIST, FieldBaseTypeObject } from "../types/BaseTypes";

const FieldBuilder = ({
  onAddField,
  sections = [],
  currentSection,
  onCreateSection,
  onSelectSection,
  existingFields = [],
}: {
  onAddField: (f: DynamicField) => void;
  sections?: Section[];
  currentSection?: Section | undefined;
  onCreateSection?: (name: string) => Section | undefined;
  onSelectSection?: (id: string) => void;
  existingFields?: DynamicField[];
}) => {
  const [label, setLabel] = useState("");
  const [section, setSection] = useState<string>(currentSection?.name || "");
  const [type, setType] = useState<FieldTypeObject>(FIELD_TYPES.TEXT);
  const [dbType, setDbType] = useState<FieldBaseTypeObject>(FIELD_BASE_TYPES.TEXT);
  const [optionsInput, setOptionsInput] = useState("");
  const [optionsValuesInput, setOptionsValuesInput] = useState("");
  
  // États pour la logique conditionnelle
  const [showConditional, setShowConditional] = useState(false);
  const [conditionalParent, setConditionalParent] = useState("");
  const [conditionalOperator, setConditionalOperator] = useState<'equals' | 'not_equals' | 'contains'>('equals');
  const [conditionalValue, setConditionalValue] = useState("");

  const handleAdd = () => {
    if (!sections || sections.length === 0) {
      alert("Veuillez créer au moins une section avant d'ajouter un champ.");
      return;
    }

    if (!label.trim()) {
      alert("Veuillez saisir un label pour le champ.");
      return;
    }
    
    if (
      (type.value === "select" || type.value === "radio" || type.value === "checkbox") &&
      (dbType.value === "number" || dbType.value === "decimal")
    ) {
      const values = optionsValuesInput.split(",").map((o) => o.trim()).filter(Boolean);

      const allNumeric = values.every((val) => {
        const num = parseFloat(val);
        return !isNaN(num) && (dbType.value === "decimal" || Number.isInteger(num));
      });

      if (!allNumeric) {
        alert(
          `Erreur: Les valeurs doivent être des ${
            dbType.value === "decimal" ? "nombres décimaux" : "nombres entiers"
          }`
        );
        return;
      }
    }

    // Validation de la condition
    if (showConditional && (!conditionalParent || !conditionalValue.trim())) {
      alert("Veuillez configurer correctement la condition d'affichage.");
      return;
    }

    let sectionObj: Section | undefined = undefined;
    const secName = section.trim();
    if (!secName) {
      alert("Veuillez sélectionner ou créer une section avant d'ajouter un champ.");
      return;
    }
    sectionObj = sections.find((s) => s.name === secName);
    if (!sectionObj) {
      if (onCreateSection) {
        const created = onCreateSection(secName);
        if (!created) {
          alert("Veuillez créer la section avant d'ajouter un champ.");
          return;
        }
        sectionObj = created;
      } else {
        alert("Veuillez créer la section avant d'ajouter un champ.");
        return;
      }
    }

    // Création du champ dynamique
    const newField: DynamicField = {
      id:
        typeof crypto !== "undefined" && (crypto as any).randomUUID
          ? (crypto as any).randomUUID()
          : Date.now().toString(),
      label: label.trim(),
      section: sectionObj,
      type,
      dbType,

      options: ["select", "radio", "checkbox"].includes(type.value)
        ? optionsInput.split(",").map((o) => o.trim()).filter(Boolean)
        : undefined,

      optionsValues:
        ["select", "radio", "checkbox"].includes(type.value) && optionsValuesInput.trim()
          ? optionsValuesInput.split(",").map((o) => o.trim()).filter(Boolean)
          : undefined,
    };

    // Ajouter la logique conditionnelle si configurée
    if (showConditional && conditionalParent && conditionalValue.trim()) {
      newField.conditionalLogic = {
        fieldId: conditionalParent,
        operator: conditionalOperator,
        value: conditionalValue.trim()
      };
    }

    onAddField(newField);

    // Reset du formulaire
    setLabel("");
    setSection(currentSection?.name || "");
    setType(FIELD_TYPES.TEXT);
    setDbType(FIELD_BASE_TYPES.TEXT);
    setOptionsInput("");
    setOptionsValuesInput("");
    setShowConditional(false);
    setConditionalParent("");
    setConditionalOperator('equals');
    setConditionalValue("");
  };

  // Filtrer les champs qui peuvent avoir des enfants conditionnels
  const conditionalEligibleFields = existingFields.filter(f => 
    ['select', 'radio', 'checkbox'].includes(f.type.value)
  );

  // Obtenir les options du champ parent sélectionné
  const getParentOptions = (): string[] => {
    if (!conditionalParent) return [];
    const parentField = existingFields.find(f => f.id === conditionalParent);
    return parentField?.options || [];
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="mb-2">
          <label className="form-label">Section</label>
          <div className="d-flex gap-2">
            <input
              className="form-control"
              type="text"
              placeholder="ex: Informations personnelles"
              value={section}
              onChange={(e) => setSection(e.target.value)}
            />
            <div className="text-end">
              {onCreateSection && (
                <button
                  type="button"
                  className="btn btn-add-field"
                  onClick={() => {
                    onCreateSection(section);
                    setSection("");
                  }}
                >
                  Créer
                </button>
              )}
            </div>
          </div>
          {sections && sections.length > 0 && (
            <div className="mt-2">
              <small className="text-muted d-block">Sections actives :</small>
              <div className="d-flex flex-wrap gap-2 mt-1 section-list">
                {sections.map((s) => (
                  <div key={s.id} className="d-inline-flex align-items-center">
                    <button
                      type="button"
                      title="Choisir cette section"
                      className={`btn btn-sm ${currentSection && currentSection.id === s.id ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => {
                        setSection(s.name);
                        if (onSelectSection) onSelectSection(s.id);
                      }}
                    >
                      {s.name}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <h5 className="card-title">Ajouter un champ</h5>
        
        {/* Label */}
        <div className="mb-2">
          <label className="form-label">Label</label>
          <input
            className="form-control"
            type="text"
            placeholder="Nom du champ"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>

        {/* Type d'affichage */}
        <div className="mb-2">
          <label className="form-label">Type d'affichage</label>
          <select
            className="form-select"
            value={JSON.stringify(type)}
            onChange={(e) => setType(JSON.parse(e.target.value))}
          >
            {FIELD_TYPES_LIST.map((t) => (
              <option key={t.value} value={JSON.stringify(t)}>{t.label}</option>
            ))}
          </select>
        </div>

        {/* Type des données / valeurs en base (uniquement pour select, radio, checkbox) */}
        {(type.value === "select" || type.value === "checkbox" || type.value === "radio") && (
          <div className="mb-2">
            <label className="form-label">Type des valeurs en base</label>
            <select
              className="form-select"
              value={JSON.stringify(dbType)}
              onChange={(e) => setDbType(JSON.parse(e.target.value))}
            >
              {FIELD_BASE_TYPES_LIST.map((t) => (
                <option key={t.value} value={JSON.stringify(t)}>{t.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Options (uniquement pour select, radio, checkbox) */}
        {(type.value === "select" || type.value === "radio" || type.value === "checkbox") && (
          <div>
            {/* Libellés */}
            <div className="mb-2">
              <label className="form-label">Libellés (séparés par des virgules)</label>
              <input
                className="form-control"
                type="text"
                placeholder="ex: Homme,Femme,Autre"
                value={optionsInput}
                onChange={(e) => setOptionsInput(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Section conditionnelle */}
        {conditionalEligibleFields.length > 0 && (
          <div className="mb-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="showConditional"
                checked={showConditional}
                onChange={(e) => setShowConditional(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showConditional">
                <strong>Champ conditionnel</strong> (affiché selon une condition)
              </label>
            </div>
          </div>
        )}

        {showConditional && (
          <div className="card bg-light p-3 mb-3">
            <h6 className="mb-3">Configuration de la condition</h6>
            
            <div className="mb-3">
              <label className="form-label">
                <strong>Afficher ce champ si :</strong>
              </label>
              <select
                className="form-control"
                value={conditionalParent}
                onChange={(e) => {
                  setConditionalParent(e.target.value);
                  setConditionalValue("");
                }}
              >
                <option value="">-- Sélectionner un champ parent --</option>
                {conditionalEligibleFields.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.label} ({f.type.label})
                  </option>
                ))}
              </select>
            </div>

            {conditionalParent && (
              <>
                <div className="mb-3">
                  <label className="form-label">
                    <strong>Opérateur :</strong>
                  </label>
                  <select
                    className="form-control"
                    value={conditionalOperator}
                    onChange={(e) => setConditionalOperator(e.target.value as any)}
                  >
                    <option value="equals">Est égal à</option>
                    <option value="not_equals">Est différent de</option>
                    {existingFields.find(f => f.id === conditionalParent)?.type.value === 'checkbox' && (
                      <option value="contains">Contient (checkbox)</option>
                    )}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <strong>Valeur déclenchante :</strong>
                  </label>
                  <select
                    className="form-control"
                    value={conditionalValue}
                    onChange={(e) => setConditionalValue(e.target.value)}
                  >
                    <option value="">-- Sélectionner une valeur --</option>
                    {getParentOptions().map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <small className="text-muted">
                    Le champ sera affiché uniquement si la condition est respectée
                  </small>
                </div>
              </>
            )}
          </div>
        )}

        {/* Bouton */}
        <div className="text-end">
          <button className="btn btn-add-field" onClick={handleAdd}>
            Ajouter le champ
          </button>
        </div>
      </div>
    </div>
  );
};

export default FieldBuilder;